import React, { useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import { studentsService, type StudentDto } from '@/services/students';
import { authService, type StudentInvitationDto } from '@/services/auth';
import { useToast } from '@/components/ui/use-toast';
import {
  PromoCard,
  PromoCardBody,
  PromoCardDesc,
  PromoCardHeader,
  PromoCardTitle,
} from '@/components/promo/PromoCard';

const displayStudent = (s: StudentDto) =>
  [s.firstName, s.lastName].filter(Boolean).join(' ').trim() || s.email || 'Student';

const TeacherStudentsPage: React.FC = () => {
  const { toast } = useToast();
  const [students, setStudents] = useState<StudentDto[]>([]);
  const [invitations, setInvitations] = useState<StudentInvitationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const [studentsData, invitationsData] = await Promise.all([
        studentsService.getStudents(),
        authService.getStudentInvitations(),
      ]);
      setStudents(Array.isArray(studentsData) ? studentsData : []);
      setInvitations(Array.isArray(invitationsData) ? invitationsData : []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load students', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      setInviting(true);
      const res = await authService.inviteStudent(inviteEmail.trim());
      setInviteEmail('');
      setLastInviteLink(res.invitationUrl);
      await load();
      toast({ title: 'Invitation sent', description: `Invite created for ${res.email}` });
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast({
        title: 'Error',
        description: message || 'Failed to invite student',
        variant: 'destructive',
      });
    } finally {
      setInviting(false);
    }
  };

  const pendingInvites = invitations.filter(
    (i) => i.status?.toLowerCase() === 'pending'
  );

  return (
    <div className="space-y-4">
      <PromoCard>
        <PromoCardHeader>
          <PromoCardTitle icon={<Users size={20} strokeWidth={2} />}>Students</PromoCardTitle>
          <PromoCardDesc>Invite students by email and manage everyone in your studio.</PromoCardDesc>
        </PromoCardHeader>
        <PromoCardBody>
          <div className="studio-invite">
            <h4>Invite by email</h4>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="student@email.com"
              className="studio-field mb-2 text-sm font-normal"
              onKeyDown={(e) => e.key === 'Enter' && void handleInvite()}
            />
            <button
              type="button"
              className="studio-send"
              disabled={!inviteEmail.trim() || inviting}
              onClick={() => void handleInvite()}
            >
              {inviting ? 'Sending…' : 'Send invite'}
            </button>
            {lastInviteLink && (
              <p className="studio-meta mt-2">
                <button type="button" className="text-violet-700 font-semibold" onClick={() => void navigator.clipboard.writeText(lastInviteLink)}>
                  Copy invite link
                </button>
              </p>
            )}
          </div>
        </PromoCardBody>
      </PromoCard>

      <PromoCard>
        <PromoCardHeader>
          <PromoCardTitle>Your roster</PromoCardTitle>
          <PromoCardDesc>
            {students.length} active · {pendingInvites.length} pending invite
            {pendingInvites.length !== 1 ? 's' : ''}
          </PromoCardDesc>
        </PromoCardHeader>
        <PromoCardBody>
          {loading ? (
            <p className="promo-empty">Loading…</p>
          ) : students.length === 0 && pendingInvites.length === 0 ? (
            <p className="promo-empty">No students yet — send your first invite above.</p>
          ) : (
            <div className="dash-stack">
              {students.map((s) => (
                <div key={s.id} className="booking-tile">
                  <div>
                    <div className="t1">{displayStudent(s)}</div>
                    <div className="t2">{s.email || 'Enrolled student'}</div>
                  </div>
                  <span className="badge ok">ACTIVE</span>
                </div>
              ))}
              {pendingInvites.map((inv) => (
                <div key={inv.id} className="booking-tile">
                  <div>
                    <div className="t1">{inv.email}</div>
                    <div className="t2">Invitation pending</div>
                  </div>
                  <span className="badge">PENDING</span>
                </div>
              ))}
            </div>
          )}
        </PromoCardBody>
      </PromoCard>
    </div>
  );
};

export default TeacherStudentsPage;
